import { Form, Formik } from "formik";
import { useState } from "react";

import { PatientInsurance, InsurancePlan, Lab } from "pwapi";

import {
  insuranceSchema,
  insuranceWarningSchema,
} from "shared/utils/caseSchema";
import { getValidationWarnings } from "shared/utils/validationUtils";

import { useAppDispatch } from "app/hooks";
import {
  createInsurance,
  updateInsurance,
} from "app/reducers/admin/actions/patientActions";
import BaseInsuranceForm from "ui/CaseForms/InsuranceForm";

const InsuranceForm = ({
  insurance,
  patientId,
  index,
  primary = false,
  lab,
  onHandleDeleteInsurance,
  setWarningDialogProps,
}: {
  insurance: PatientInsurance;
  patientId?: number;
  index: number;
  primary?: boolean;
  lab?: Lab;
  onHandleDeleteInsurance: Function;
  setWarningDialogProps: Function;
}) => {
  const dispatch = useAppDispatch();

  const initialValues: PatientInsurance = Object.assign(
    {
      relationship_to_patient: "",
      insured_first_name: "",
      insured_last_name: "",
      insured_date_of_birth: "",
      group_employer_id: "",
      policy_number: "",
      insurance_company_name: "",
      insurance_company_state: "",
      insurance_company_city: "",
      insurance_company_zipcode: "",
      plan: null,
      has_abn: false,
      preapproval_id: "",
    },
    insurance || {},
    {}
  );

  const onHandleDelete = () => {
    setWarningDialogProps({
      resourceName: "Additional Insurance",
      resourceId: insurance?.id,
      onHandleDelete: onHandleDeleteInsurance,
    });
  };

  const [warnings, setWarnings] = useState({});

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      onSubmit={(values) => {
        //Removed requestBody out to the common variables between the two api calls
        const requestBody = {
          ...values,
          ...(values.relationship_to_patient === "SELF"
            ? {
                insured_first_name: undefined,
                insured_last_name: undefined,
                insured_date_of_birth: undefined,
              }
            : {}),
          insured_date_of_birth: values.insured_date_of_birth || null,
          // Plan should not be provided to the API on update or create if the insurance is manual or the lab uses manual insurance.
          plan:
            insurance.is_manual || lab?.use_manual_insurance
              ? undefined
              : values.plan?.id
              ? { id: values.plan?.id }
              : null,
        };

        patientId
          ? dispatch(
              insurance?.id
                ? updateInsurance({
                    id: insurance.id,
                    requestBody: {
                      ...requestBody,
                      id: insurance.id,
                    },
                  })
                : createInsurance({
                    requestBody: {
                      ...requestBody,
                      patient_id: patientId,
                      primary_provider: !!primary,
                    },
                  })
            )
          : console.error("No patient id to create/update an insurance");
      }}
      validate={(values: any) => {
        insuranceWarningSchema
          .validate(values, {
            abortEarly: false,
          })
          .then(() => {
            setWarnings({});
          })
          .catch((err) => {
            setWarnings(getValidationWarnings(err));
          });
      }}
      validateOnBlur={false}
      validationSchema={insuranceSchema}
      validateOnMount={true}
    >
      {({ errors, values, dirty, setFieldValue, setValues, touched }) => (
        <Form autoComplete="off">
          <BaseInsuranceForm
            i={index}
            insurance={values}
            primary={primary}
            errors={errors}
            touched={touched}
            warnings={warnings}
            useInsurancePlan={
              !lab?.use_manual_insurance && !insurance.is_manual
            }
            adminLabId={lab?.id}
            isAdmin
            displaySubmit
            dirty={dirty}
            onHandleDelete={onHandleDelete}
            onHandleChangeInsurancePlan={(newValue: InsurancePlan) => {
              setFieldValue("plan", newValue);
            }}
            onHandleChangeRelationship={(value: string) => {
              setValues({
                ...values,
                insured_date_of_birth: "",
                insured_first_name: "",
                insured_last_name: "",
                relationship_to_patient: value,
              });
            }}
          />
        </Form>
      )}
    </Formik>
  );
};

export default InsuranceForm;
